import { Dialog, DialogTitle, DialogContent } from "@myplant-io/mui";
import LoadingButton from "@mui/lab/LoadingButton";
import UploadIcon from "@mui/icons-material/Upload";
import SaveIcon from "@mui/icons-material/Save";
import { defineMessages, useIntl } from "react-intl";
import { yup } from "@myplant-io/utils";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  Alert,
  DialogActions,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { commonMessages } from "../../commonMessages";
import { Mode, ModeStoreEntry } from "../../types";
import { useEffect } from "react";

const FORM_ID = "mode-publish-dialog";

export const messages = defineMessages({
  title: {
    id: "modeStoreDialog.title",
    defaultMessage: "Store settings",
  },
  disclaimer: {
    id: "modeStoreDialog.disclaimer",
    defaultMessage:
      "By publishing you accept that the agent can be used by any INNIO employee.\nMake sure the included documents, knowledge basis and instructions are allowed to be shared across INNIO.",
  },
});

type PublishData = {
  id?: string;
  modeId: string;
  description: string;
};

type Props = {
  modeStoreEntry?: ModeStoreEntry;
  mode?: Mode;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PublishData) => Promise<void>;
};

export function ModeStoreUpdateDialog({
  modeStoreEntry,
  mode,
  open,
  onClose,
  onSubmit,
}: Props) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const { formatMessage: t } = useIntl();
  const schema = yup
    .object({
      id: yup.string(),
      modeId: yup.string().required(),
      description: yup.string().required(),
    })
    .required();

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting, isSubmitted, isValid },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      description: "",
    },
  });

  useEffect(() => {
    reset({
      id: modeStoreEntry?._id,
      modeId: modeStoreEntry?.mode?._id ?? mode?._id,
      description: modeStoreEntry?.description,
    });
  }, [modeStoreEntry, mode, reset]);

  const _onSubmit: SubmitHandler<PublishData> = async (data) => {
    await onSubmit({
      id: data.id,
      modeId: data.modeId,
      description: data.description,
    });
  };

  return (
    <Dialog open={open} fullWidth fullScreen={isSmall} maxWidth="md">
      <DialogTitle onClose={onClose}>{t(messages.title)}</DialogTitle>
      <DialogContent>
        <Alert
          icon={false}
          severity="warning"
          className="whitespace-pre-line mb-4"
        >
          {t(messages.disclaimer)}
        </Alert>
        <form
          id={FORM_ID}
          onSubmit={handleSubmit(_onSubmit)}
          className="flex flex-col"
        >
          <Controller
            control={control}
            name="description"
            render={({ field, fieldState }) => (
              <>
                <Typography variant="h6" id="input-slider" gutterBottom>
                  {`${t(commonMessages.description)}*`}
                </Typography>
                <TextField
                  {...field}
                  multiline
                  rows={3}
                  label={t(commonMessages.description)}
                  value={field.value ?? ""}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                />
              </>
            )}
          />
        </form>
      </DialogContent>
      <DialogActions>
        {!modeStoreEntry ? (
          <LoadingButton
            form={FORM_ID}
            type="submit"
            variant="outlined"
            loading={isSubmitting}
            loadingPosition="start"
            startIcon={<UploadIcon />}
            disabled={
              mode?.read_only || isSubmitting || (isSubmitted && !isValid)
            }
          >
            {t(commonMessages.publish)}
          </LoadingButton>
        ) : (
          <LoadingButton
            form={FORM_ID}
            type="submit"
            variant="outlined"
            loading={isSubmitting}
            loadingPosition="start"
            startIcon={<SaveIcon />}
            disabled={
              mode?.read_only || isSubmitting || (isSubmitted && !isValid)
            }
          >
            {t(commonMessages.save)}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
